/**
 * useMaterialSelection Hook
 * 
 * Handle mesh click selection & hover effects:
 * - Click mesh to select material
 * - Hover effects (cyan glow)
 * - Selection feedback (visual outline)
 * - Auto-save default state
 * 
 * @param {Object} refs - Scene refs
 * @param {Object} undoRedo - Undo/redo hook
 * @param {Function} setIsMaterialPanelOpen - Panel toggle
 * @param {Function} setIsHistoryPanelOpen - History panel toggle
 * @returns {Object} Selection state dan handlers
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { detectMeshUnderCursor, isAllowedMaterialName } from '@utils/raycasting'

export function useMaterialSelection(
  refs,
  undoRedo,
  setIsMaterialPanelOpen,
  setIsHistoryPanelOpen
) {
  const {
    containerRef,
    cameraRef,
    sceneRef,
    raycasterRef,
    mouseRef,
    modelRef,
    selectedCameraRef,
    groundPlaneRef,
  } = refs

  const [selectedMaterial, setSelectedMaterial] = useState(null)
  const [hoveredMesh, setHoveredMesh] = useState(null)
  const [isDragging, setIsDragging] = useState(false)

  const selectionOutlineRef = useRef(null)
  const hoverOutlineRef = useRef(null)
  const previousHoveredMeshRef = useRef(null)

  // Handle click on mesh
  const handleMeshClick = useCallback(
    (event) => {
      if (isDragging) return

      const mesh = detectMeshUnderCursor(
        event,
        containerRef,
        cameraRef,
        sceneRef,
        raycasterRef,
        mouseRef,
        modelRef,
        selectedCameraRef
      )

      if (mesh && mesh.material) {
        const material = Array.isArray(mesh.material)
          ? mesh.material[0]
          : mesh.material

        const materialName = material.name || ''
        if (!isAllowedMaterialName(materialName)) {
          return
        }

        setSelectedMaterial({
          mesh: mesh,
          material: material,
          meshName: mesh.name || 'Unnamed Mesh',
        })

        // Auto-open material panel
        setIsMaterialPanelOpen(true)
        setIsHistoryPanelOpen(false)

        console.log('Material selected:', {
          name: mesh.name,
          material: material.type,
        })

        // Save default state
        setTimeout(() => {
          if (
            !undoRedo.snapshotCacheRef.current.find(
              (s) => s.description === 'DEFAULT - Initial State'
            )
          ) {
            const defaultSnapshot = {
              _serial: 0,
              description: 'DEFAULT - Initial State',
              colorHex: material.color
                ? `#${material.color.getHexString()}`
                : null,
              emissiveHex: material.emissive
                ? `#${material.emissive.getHexString()}`
                : null,
              metalness:
                material.metalness !== undefined
                  ? Number(material.metalness)
                  : null,
              roughness:
                material.roughness !== undefined
                  ? Number(material.roughness)
                  : null,
              opacity:
                material.opacity !== undefined ? Number(material.opacity) : null,
              transparent: Boolean(material.transparent),
              wireframe: Boolean(material.wireframe),
              emissiveIntensity:
                material.emissiveIntensity !== undefined
                  ? Number(material.emissiveIntensity)
                  : null,
              side: material.side,
              _materialRef: material,
              _timestamp: Date.now(),
              _isDefault: true,
            }

            undoRedo.snapshotCacheRef.current = [defaultSnapshot]
            undoRedo.snapshotIndexRef.current = 0
            console.log('🎬 DEFAULT state saved as History #0')
          }
        }, 50)
      } else {
        setSelectedMaterial(null)
      }
    },
    [
      isDragging,
      containerRef,
      cameraRef,
      sceneRef,
      raycasterRef,
      mouseRef,
      modelRef,
      selectedCameraRef,
      setIsMaterialPanelOpen,
      setIsHistoryPanelOpen,
      undoRedo,
    ]
  )

  // Visual feedback for selected mesh
  useEffect(() => {
    if (!sceneRef.current || !window.THREE_MODULE) return

    const THREE = window.THREE_MODULE

    // Remove old outline
    if (selectionOutlineRef.current) {
      if (selectionOutlineRef.current.parent) {
        selectionOutlineRef.current.parent.remove(selectionOutlineRef.current)
      }
      if (selectionOutlineRef.current.userData.materials) {
        selectionOutlineRef.current.userData.materials.forEach((mat) =>
          mat.dispose()
        )
      }
      selectionOutlineRef.current = null
    }

    // Add new selection outline
    if (selectedMaterial && selectedMaterial.mesh) {
      const mesh = selectedMaterial.mesh

      try {
        const outlineGroup = new THREE.Group()

        const glowMat = new THREE.MeshBasicMaterial({
          color: 0x00ffff,
          transparent: true,
          opacity: 0.5,
          side: THREE.FrontSide,
          depthTest: true,
          depthWrite: false,
        })
        const glowMesh = new THREE.Mesh(mesh.geometry, glowMat)
        glowMesh.scale.setScalar(1.005)
        outlineGroup.add(glowMesh)

        mesh.add(outlineGroup)
        selectionOutlineRef.current = outlineGroup
        selectionOutlineRef.current.userData.materials = [glowMat]

        // Auto fade-out
        const fadeOutTimeout = setTimeout(() => {
          const startTime = Date.now()
          const startOpacity = glowMat.opacity

          const fadeAnimation = () => {
            if (!glowMat || glowMat.disposed) return

            const elapsed = Date.now() - startTime
            const progress = Math.min(elapsed / 200, 1)
            const easeOut = 1 - Math.pow(1 - progress, 3)
            glowMat.opacity = startOpacity * (1 - easeOut)

            if (progress < 1) {
              requestAnimationFrame(fadeAnimation)
            } else {
              if (outlineGroup && outlineGroup.parent) {
                outlineGroup.visible = false
              }
            }
          }

          fadeAnimation()
        }, 400)

        selectionOutlineRef.current.userData.fadeOutTimeout = fadeOutTimeout
      } catch (error) {
        console.error('Error creating selection outline:', error)
      }
    }

    return () => {
      if (selectionOutlineRef.current) {
        if (selectionOutlineRef.current.userData.fadeOutTimeout) {
          clearTimeout(selectionOutlineRef.current.userData.fadeOutTimeout)
        }
        if (selectionOutlineRef.current.parent) {
          selectionOutlineRef.current.parent.remove(selectionOutlineRef.current)
        }
        if (selectionOutlineRef.current.userData.materials) {
          selectionOutlineRef.current.userData.materials.forEach((mat) =>
            mat.dispose()
          )
        }
        selectionOutlineRef.current = null
      }
    }
  }, [selectedMaterial, sceneRef])

  // Visual feedback for hovered mesh
  useEffect(() => {
    if (!sceneRef.current || !window.THREE_MODULE) return

    const THREE = window.THREE_MODULE

    // Check if same mesh
    if (hoveredMesh && previousHoveredMeshRef.current) {
      if (hoveredMesh.uuid === previousHoveredMeshRef.current.uuid) {
        return
      }
    }

    previousHoveredMeshRef.current = hoveredMesh

    // Remove old hover outline
    if (hoverOutlineRef.current) {
      if (hoverOutlineRef.current.userData.fadeOutTimeout) {
        clearTimeout(hoverOutlineRef.current.userData.fadeOutTimeout)
      }
      if (hoverOutlineRef.current.parent) {
        hoverOutlineRef.current.parent.remove(hoverOutlineRef.current)
      }
      if (hoverOutlineRef.current.userData.materials) {
        hoverOutlineRef.current.userData.materials.forEach((mat) =>
          mat.dispose()
        )
      }
      hoverOutlineRef.current = null
    }

    // Add new hover outline
    if (hoveredMesh) {
      try {
        const hoverGroup = new THREE.Group()

        const glowMat = new THREE.MeshBasicMaterial({
          color: 0x00ffff,
          transparent: true,
          opacity: 0.5,
          side: THREE.FrontSide,
          depthTest: true,
          depthWrite: false,
        })
        const glowMesh = new THREE.Mesh(hoveredMesh.geometry, glowMat)
        glowMesh.scale.setScalar(1.005)
        hoverGroup.add(glowMesh)

        hoveredMesh.add(hoverGroup)
        hoverOutlineRef.current = hoverGroup
        hoverOutlineRef.current.userData.materials = [glowMat]

        // Auto fade-out
        const fadeOutTimeout = setTimeout(() => {
          const startTime = Date.now()
          const startOpacity = glowMat.opacity

          const fadeAnimation = () => {
            if (!glowMat || glowMat.disposed) return

            const elapsed = Date.now() - startTime
            const progress = Math.min(elapsed / 200, 1)
            const easeOut = 1 - Math.pow(1 - progress, 3)
            glowMat.opacity = startOpacity * (1 - easeOut)

            if (progress < 1) {
              requestAnimationFrame(fadeAnimation)
            } else {
              if (hoverGroup && hoverGroup.parent) {
                hoverGroup.visible = false
              }
            }
          }

          fadeAnimation()
        }, 400)

        hoverOutlineRef.current.userData.fadeOutTimeout = fadeOutTimeout
      } catch (error) {
        console.error('Error creating hover outline:', error)
      }
    }

    return () => {
      if (hoverOutlineRef.current) {
        if (hoverOutlineRef.current.userData.fadeOutTimeout) {
          clearTimeout(hoverOutlineRef.current.userData.fadeOutTimeout)
        }
        if (hoverOutlineRef.current.parent) {
          hoverOutlineRef.current.parent.remove(hoverOutlineRef.current)
        }
        if (hoverOutlineRef.current.userData.materials) {
          hoverOutlineRef.current.userData.materials.forEach((mat) =>
            mat.dispose()
          )
        }
        hoverOutlineRef.current = null
      }
    }
  }, [hoveredMesh, sceneRef])

  return {
    selectedMaterial,
    setSelectedMaterial,
    hoveredMesh,
    setHoveredMesh,
    isDragging,
    setIsDragging,
    handleMeshClick,
    selectionOutlineRef,
    hoverOutlineRef,
  }
}

